import React, { useState, useRef, useEffect, useMemo } from "react";
import ParticipantView from "../components/ParticipantView";
import {
  useMeeting,
  useCharacter,
  usePubSub,
  createMicrophoneAudioTrack,
} from "@videosdk.live/react-sdk";
import useIsMobile from "../utils/useIsMobile";
import { logType, useJourney } from "../utils/common";
import { useAppContext } from "../AppContextDef";
import MainCard from "../components/MainCard";
import CharacterView from "../components/CharacterView";
import ConfirmBox from "../components/ConfirmBox";
import { useTranslation } from "react-i18next";
import useIsTab from "../utils/useIsTab";
import useAuthFetch from "../utils/useAuthFetch";
import imageSrc from "../repeat_audio_icon.png";
import usePubsubFunction from "../utils/usePubsubFunction";

const VideoConsent = ({
  handleVideoConsentSuccess,
  capturedImage,
  characterJoined,
  currentPage,
  getScreenshotImage,
  accuracyRef,
  setCurrentPage,
  setCapturedImage,
  abortSignal,
}) => {
  const {
    journeyConfig,
    applicationNo,
    allDetails,
    journeyData,
    selectedLanguageCode,
    HeaderSectionHeight,
    deviceData,
  } = useAppContext();
  let videoConsentText = journeyConfig?.clientScript;
  // const [isBadNetwork, setIsBadNetwork] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const customFetch = useAuthFetch();
  const { logJourney, endJourney, updateJourney } = useJourney();
  const [buttonSectionHeightRef, setButtonSectionHeightRef] = useState(null);
  const [textSectionHeightRef, setTextSectionHeightRef] = useState(null);
  const ButtonSectionHeight = useMemo(
    () => buttonSectionHeightRef?.offsetHeight,
    [buttonSectionHeightRef]
  );
  const TextSectionHeight = useMemo(
    () => textSectionHeightRef?.offsetHeight,
    [textSectionHeightRef]
  );
  const retryCountRef = useRef(0);
  const [accuracy, setAccuracy] = useState(null);
  const [customerImageRetry, setCustomerImageRetry] = useState(null)
  const [didDeviceChange, setDidDeviceChange] = useState(false);
  const [returnButtonClicked, setReturnButtonClicked] = useState(false);
  const [timer, setTimer] = useState(journeyConfig?.allowedTimeForScript);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isStartClick, setIsStartClick] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [isCharacterSpeaking, setIsCharacterSpeaking] = useState(false);
  const [isVerifyingRecording, setIsVerifieyingRecording] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [eventCome, setEventCome] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDeviceChange, setOpenDeviceChange] = useState(false);
  const [openErr, setOpenErr] = useState(false);
  const [openTextMatchErr, setOpenTextMatchErr] = useState(false);
  const [isRecordAgain, setIsRecordAgain] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const clickRef = useRef();
  const { t, i18n } = useTranslation();
  const { publish: timerRunningPublish } = usePubSub(`TIMER_RUNNING`);
  const { publish: timerDataPublish } = usePubSub("TIMER_DATA");
  const { publish: startVideoPublish } = usePubSub("START_VIDEO");
  const { publish: openVideoErrPublish } = usePubSub(
    "VIDEO_RECORDING_ATTEMPT_ERR"
  );
  const { publish: openVideoFailPublish } = usePubSub(
    "VIDEO_RECORDING_ATTEMPT_FAIL_ERR"
  );

  const { publish: textMatchingFailPublish } = usePubSub(
    "TEXT_MATCHING_FAILED"
  );

  const latestSpokenTextRef = useRef(null);

  const { publish: openDeviceChangePublish } = usePubSub("DEVICE_CHANGED");

  const { publish: endJourneyAPIWaitingPublish } = usePubSub(
    "END_JOURNEY_API_WAITING"
  );
  const pubsubPublishMethod = usePubsubFunction()


  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };
  const timerRef = useRef();

  useEffect(() => {
    timerRef.current = isTimerRunning;
  }, [isTimerRunning]);

  useEffect(() => {
    clickRef.current = clickCount;
  }, [clickCount]);

  useEffect(() => {
    changeLanguage(selectedLanguageCode);
  }, [selectedLanguageCode]);

  const intervalRef = useRef(null);
  const { toggleMic } = useMeeting();
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  let isMobileOrTab = isMobile || isTab;
  const { end, localParticipant } = useMeeting();
  const customerImageRef = useRef(null);

  // usePubSub("BAD_NETWORK", {
  //   onMessageReceived: ({ message }) => {
  //     setIsBadNetwork(message.isBadNetwork)
  //   },
  // });


  // useEffect(() => {
  //   if (isBadNetwork && isTimerRunning) {
  //     timerDataPublish(0, { persist: true });
  //     timerRunningPublish({ isRunning: false }, { persist: true });
  //     if (intervalRef.current) {
  //       clearInterval(intervalRef.current);
  //       intervalRef.current = null;
  //     }
  //     setIsTimerRunning(false);
  //     setClickCount(clickRef.current - 1);
  //   }

  // }, [isBadNetwork, isTimerRunning])

  const endJourneyAPI = async (journeyStatus) => {
    const status = await endJourney({
      journeyStatus: journeyStatus,
      customerImage: customerImageRef.current ? customerImageRef.current : null,
    });

    if (status === 200 && journeyStatus === "COMPLETED") {
      pubsubPublishMethod(endJourneyAPIWaitingPublish, "journeyend", { persist: true }, { isJourneyEnd: false });
      end();
      setIsVerifieyingRecording(false);
      handleVideoConsentSuccess();
    }
  };

  const handleMoveToQna = async () => {

    const { response, status, message } = await updateJourney({
      customerImage: customerImageRef.current ? customerImageRef.current : null,
    });

    if (status === 200) {
      setIsVerifieyingRecording(false);
      handleVideoConsentSuccess();
    }
  }

  useEffect(() => {
    // setUserMessages("");
    accuracyRef.current = null;
    logJourney({
      type: logType.NAVIGATION,
      message: "Navigated to Video Consent Page",
    });
  }, []);

  const captureCustomerImage = async () => {
    try {
      const image = await localParticipant.captureImage();
      if (image) {
        customerImageRef.current = `data:image/jpeg;base64,${image}`;
      }
      setCustomerImageRetry((prev) => prev + 1);
    } catch (err) {
      console.log("Error in capturing image", err);
      logJourney({
        message: `Something went wrong could not capture customer image ${err} `,
        type: logType.ERROR
      })
    }
  }

  useEffect(() => {
    if (agreed && customerImageRef.current === null && customerImageRetry < 5) {
      captureCustomerImage()
    }
    else if (customerImageRef.current === null && customerImageRetry >= 5) {
      logJourney({
        message: "Maximum Attempts for retrying customer image capture exhausted.",
        type: logType.ERROR
      })
    }

  }, [customerImageRetry, agreed])

  const textMatching = async ({ spokenText }) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/v1/application/matchText`;
      const method = "POST";
      const headers = {
        authorization: allDetails?.authToken,
        "Content-Type": "application/json",
      };
      const bodyContent = {
        translatedReferenceText: journeyConfig?.translatedClientScript ?? null,
        referenceText: videoConsentText,
        spokenText: spokenText?.text,
        configId: journeyConfig?.configId,
        journeyId: journeyData?.journeyId,
      };

      const { response, status, message } = await customFetch(
        url,
        method,
        headers,
        bodyContent
      );
      if (status === 200) {
        setAccuracy(response.accuracy);
        accuracyRef.current = {
          accuracy: response.accuracy,
          success: response.success,
        };
        const url = `${process.env.REACT_APP_API_URL}/v1/textMatching/create`;
        const method = "POST";
        const headers = {
          authorization: allDetails?.authToken,
          "Content-Type": "application/json",
        };
        const bodyContent = {
          journeyId: journeyData?.journeyId,
          text: spokenText.text,
          accuracy: parseInt(response.accuracy),
          timestamp: new Date().toISOString(),
        };
        await customFetch(url, method, headers, bodyContent);
        setEventCome(true);
      } else {
        console.error("Error in fetching accuracy", message);
      }
    } catch (e) {
      if (retryCountRef.current < 2) {
        setTimeout(async () => {
          await textMatching({ spokenText: latestSpokenTextRef.current });
          retryCountRef.current = retryCountRef.current + 1;
        }, 5000);
      } else {
        pubsubPublishMethod(textMatchingFailPublish, "textMatchingFailPublish", { persist: true }, { open: true });
        setOpenTextMatchErr(true);
      }
    }
  };
  const deviceChangeRef = useRef(null);
  useEffect(() => {
    deviceChangeRef.current = didDeviceChange;
  }, [didDeviceChange]);

  useCharacter(
    {
      id: "icici-agent",
      characterMode: "auto_pilot",
      language: selectedLanguageCode,
    },
    {
      onUserMessage: async (data) => {
        if (!deviceChangeRef.current) {
          setEventCome(false);
          latestSpokenTextRef.current = data;
          await textMatching({ spokenText: data });
        }
      },
    }
  );

  const handleStart = async () => {
    if (clickCount <= 2) {
      pubsubPublishMethod(startVideoPublish, "Start", { persist: true }, { isStart: true });
      setClickCount(clickCount + 1);
      const track = await createMicrophoneAudioTrack({
        encoderConfig: "speech_standard",
        noiseConfig: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });

      toggleMic(track);
      track?.getAudioTracks()[0].addEventListener("ended", () => {
        setDidDeviceChange(true);
        if (timerRef.current) {
          handleDeviceChange();
        }
      });
      setTimer(journeyConfig?.allowedTimeForScript);
      setIsTimerRunning(true);
      setStopped(false);
      setIsStartClick(true);
      pubsubPublishMethod(timerRunningPublish, "timer", { persist: true }, { isRunning: true });

      intervalRef.current = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 1) {
            handleStop();
          }
          const newTimerValue = prevTimer - 1;
          pubsubPublishMethod(timerDataPublish, "time", { persist: true }, { time: newTimerValue }); // Send timer value to pubsub
          return newTimerValue;
        });
      }, 1000);
      logJourney({
        type: logType.ACTION,
        message: "Start Button Clicked On Video Consent Page",
      });
    } else {
      pubsubPublishMethod(openVideoFailPublish, "Video_fail", { persist: true }, { open: true });

      setOpenErr(true);
    }
  };

  const handleDeviceChange = () => {
    pubsubPublishMethod(openDeviceChangePublish, "Device_change", { persist: true }, { open: true });
    setOpenDeviceChange(true);
    pubsubPublishMethod(timerDataPublish, "time", { persist: true }, { time: 0 }); // Send timer value to pubsub
    pubsubPublishMethod(timerRunningPublish, "timerRunningPublish", { persist: true }, { isRunning: false });

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsTimerRunning(false);
    setClickCount(clickRef.current - 1);
    setDidDeviceChange(false);
  };

  const handleStop = () => {
    setIsRecordAgain(false);
    setEventCome(false);
    setStopped(true);
    setIsVerifieyingRecording(true);
    setIsTimerRunning(false);
    pubsubPublishMethod(timerDataPublish, "time", { persist: true }, { time: 0 }); // Send timer value to pubsub
    pubsubPublishMethod(timerRunningPublish, "Timer", { persist: true }, { isRunning: false });
    toggleMic();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    logJourney({
      type: logType.ACTION,
      message: "Stop Button Clicked On Video Consent Page",
    });
    if (!allDetails?.qnaTemplateId) {
      pubsubPublishMethod(endJourneyAPIWaitingPublish, "End_journey", { persist: true }, { isJourneyEnd: true });
    }
  };
  useEffect(() => {
    if (agreed && stopped && eventCome && !didDeviceChange) {
      if (accuracyRef.current?.success) {
        if (allDetails?.qnaTemplateId) {
          handleMoveToQna()
        } else {
          endJourneyAPI("COMPLETED");
        }
      } else {
        if (clickCount === 3) {
          pubsubPublishMethod(openVideoFailPublish, "open_video", { persist: true }, { open: true });
          setOpenErr(true);
        } else if (isStartClick) {
          pubsubPublishMethod(openVideoErrPublish, "open_video", { persist: true }, { open: true, accuracy: accuracyRef.current.accuracy });
          setOpen(true);
        }
        pubsubPublishMethod(endJourneyAPIWaitingPublish, "Journey_end", { persist: true }, { isJourneyEnd: false });
        setIsVerifieyingRecording(false);
      }
    }
  }, [
    agreed,
    stopped,
    eventCome,
    accuracy,
    deviceData,
    accuracyRef,
    isStartClick,
  ]);

  const isEnabled =
    agreed &&
    !isCharacterSpeaking &&
    messageSent &&
    !isTimerRunning &&
    clickCount <= 3;

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);


  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  let spacingHeight = isMobile
    ? HeaderSectionHeight + ButtonSectionHeight + 250 + 70 + 40
    : ButtonSectionHeight + 136 + 40 + TextSectionHeight + 68 + 60;

  let videoHeight = viewportHeight - spacingHeight;

  let buttonCondition = !isCharacterSpeaking && messageSent && !isTimerRunning;

  return (
    <div className="flex flex-col max-h-screen lg:h-full w-full bg-white py-0 px-0 lg:py-10 lg:px-8 xl:px-14">
      <MainCard
        mainTitle={t("Video Consent")}
        mainClassCss={
          "flex flex-1 flex-col h-screen justify-between lg:border lg:rounded-xl py-3 px-3 lg:py-7 lg:px-8 xl:px-14"
        }
        mainTitleCss={
          "lg:flex lg:flex-1 text-center justify-center hidden text-2xl lg:text-4xl text-black-200 font-extrabold mb-0"
        }
        FirstSection={
          <div
            className={`lg:mt-9 ${isMobileOrTab && "sticky top-40"
              } md:px-2 lg:px-0`}
          >
            {isMobile ? (
              <div className="flex gap-3">
                <div className="flex flex-1 rounded-2xl p-2 items-center ">
                  <div className="">
                    <p className="text-sm lg:text-base font-bold text-black-150 align-middle justify-center">
                      {`${t(`Application number`)}:`}
                    </p>
                    <p className="text-sm lg:text-base font-bold text-black-150 align-middle justify-center">
                      {`${applicationNo}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-1">
                  <ParticipantView
                    capturedImage={capturedImage}
                    setCapturedImage={setCapturedImage}
                    setAgreed={setAgreed}
                    agreed={agreed}
                    currentPage={currentPage}
                    abortSignal={abortSignal}
                    videoHeight={videoHeight}
                  />
                </div>
              </div>
            ) : (
              <ParticipantView
                capturedImage={capturedImage}
                setAgreed={setAgreed}
                agreed={agreed}
                currentPage={currentPage}
                setCapturedImage={setCapturedImage}
                abortSignal={abortSignal}
                videoHeight={videoHeight}
              />
            )}
            <CharacterView
              getScreenshotImage={getScreenshotImage}
              characterJoined={characterJoined}
              currentPage={currentPage}
              setIsCharacterSpeaking={setIsCharacterSpeaking}
              isCharacterSpeaking={isCharacterSpeaking}
              agreed={agreed}
              messageSent={messageSent}
              setMessageSent={setMessageSent}
              message={`${t(
                "Please record your video consent by clicking Start and clearly reading the text displayed on the screen. When you're done, click Stop to complete the process."
              )}`}
            />
            <div ref={setTextSectionHeightRef}>
              {isTimerRunning && (
                <div className="mt-3 md:mt-5 text-base lg:text-lg font-bold text-left lg:text-center">
                  {t("timeRemaining", { timer })}
                </div>
              )}

              {isVerifyingRecording ? (
                <div className="mt-4">
                  <div className=" flex justify-center items-center w-full h-11 ">
                    <div
                      style={{ borderTopColor: "#EA7617" }}
                      className="animate-spin  ease-linear rounded-full border-4  border-gray-100 h-12 w-12"
                    ></div>
                  </div>
                  <p className="text-center text-base lg:text-lg text-black-150 mt-3">
                    {`${t("Please wait while we verify your recording")}...`}
                  </p>
                </div>
              ) : (
                videoConsentText && (
                  <p
                    className={`text-left lg:text-center text-base lg:text-lg font-bold ${isTimerRunning ? "mt-3" : "mt-3 md:mt-6"
                      }`}
                  >
                    {videoConsentText}
                  </p>
                )
              )}
            </div>
          </div>
        }
        SecondSection={
          isVerifyingRecording ? null : (
            <div
              ref={setButtonSectionHeightRef}
              className="flex items-center justify-center text-sm md:text-base mt-8"
            >
              {!isTimerRunning ? (
                <div className={`${isEnabled && "animate-pulse"}`}>
                  <button
                    className={`disabled:bg-opacity-15 disabled:cursor-not-allowed disabled:animate-none animate-bounce  bg-orange-150 text-white p-3 rounded-md w-full font-bold lg:max-w-max`}
                    onClick={handleStart}
                    disabled={!isEnabled}
                  >

                    {t("Start")}
                  </button>
                </div>
              ) : (
                <button
                  className="bg-gray-100 disabled:cursor-not-allowed text-black-150 p-3 rounded-md border border-darkGray-150 ml-3 w-full font-bold lg:max-w-max"
                  onClick={handleStop}
                  disabled={
                    isTimerRunning && (Number(journeyConfig?.allowedTimeForScript) - Number(timer) < 10) ? true :
                      isCharacterSpeaking
                        ? true
                        : isRecordAgain
                          ? stopped
                          : stopped || !agreed
                  }
                >
                  {t("Stop")}
                </button>
              )}
              <button
                className={`bg-blue-250 text-blue-150 disabled:cursor-not-allowed flex items-center justify-center p-2 md:p-3 rounded-md ml-3 w-full text-sm md:text-base font-bold lg:max-w-max`}
                onClick={() => {
                  setMessageSent(false);
                }}
                disabled={!buttonCondition}
              >
                <img src={imageSrc} alt="icon" className="w-6 h-6" />
              </button>
            </div>
          )
        }
      />
      {open && (
        <ConfirmBox
          open={open}
          setOpen={setOpen}
          buttonText={`${t("Record again")}`}
          text={` ${t(
            "The spoken text is inaccurate. Please try recording again."
          )}`}
          buttonHandler={() => {
            setIsRecordAgain(true);
            pubsubPublishMethod(openVideoErrPublish, "Video_err", { persist: true }, { open: false });
            setIsStartClick(false);
            setOpen(false);
          }}
          extraButtonText={`${t("Return to homepage")}`}
          extraButtonHandler={async () => {
            if (returnButtonClicked) return;
            setReturnButtonClicked(true);
            await endJourneyAPI("VIDEO_CONSENT_PROCESS_ABORTED");
            end();
            pubsubPublishMethod(openVideoErrPublish, "Video_err", { persist: true }, { open: false });
            setIsStartClick(false);
            setOpen(false);
            // setCurrentPage("Intro");
            window.location.reload();
          }}
          thirdButtonText={`${t("Submit for Validation")}`}
          thirdButtonHandler={async () => {
            if (returnButtonClicked) return;
            setReturnButtonClicked(true);
            await endJourneyAPI("APPROVER_PENDING");
            end();
            pubsubPublishMethod(openVideoErrPublish, "Video_err", { persist: true }, { open: false });
            setIsStartClick(false);
            setOpen(false);
            // setCurrentPage("Intro");
            window.location.reload();
          }}
          thirdButtonCondition={accuracyRef.current.accuracy > 60 ? true : false}
        />
      )}
      {openErr && (
        <ConfirmBox
          open={openErr}
          setOpen={setOpenErr}
          buttonText={`${t("Return to homepage")}`}
          buttonHandler={async () => {
            if (returnButtonClicked) return;
            setReturnButtonClicked(true);
            await endJourneyAPI(
              "VIDEO_CONSENT_VERIFICATION_ATTEMPTS_EXHAUSTED"
            );
            end();
            pubsubPublishMethod(openVideoFailPublish, "Video_fail", { persist: true }, { open: false });
            setOpenErr(false);
            // setCurrentPage("Intro");
            window.location.reload();
          }}
          text={`${t(
            "Your video consent was not successfully recorded. Please restart the process and try again."
          )}`}
        />
      )}
      {openTextMatchErr && (
        <ConfirmBox
          open={openTextMatchErr}
          setOpen={setOpenTextMatchErr}
          text={t(
            "Oops! Some error occurred. Please reach out to our sales representative or write to us at vidassure@iciciprulife.com [ERR099]"
          )}
          buttonText={t("Return to homepage")}
          buttonHandler={async () => {
            if (returnButtonClicked) return;
            setReturnButtonClicked(true);
            await logJourney({
              type: logType.ERROR,
              message: `PIVV failed because Text could not be matched.`,
            });
            await endJourneyAPI("FAILED");
            end();
            pubsubPublishMethod(textMatchingFailPublish, "Matching_fail", { persist: true }, { open: false });
            setOpenTextMatchErr(false);
            window.location.reload();
          }}
        />
      )}
      {openDeviceChange && (
        <ConfirmBox
          open={openDeviceChange}
          setOpen={setOpenDeviceChange}
          buttonText={`${t("Record again")}`}
          buttonHandler={async () => {
            setOpenDeviceChange(false);
            pubsubPublishMethod(openDeviceChangePublish, "device_change", { persist: true }, { open: false });
          }}
          text={`${t(
            "Your video consent could not be recorded due to a device change. Please try again."
          )}`}
        />
      )}
      {/* {isBadNetwork && (
        <ConfirmBox
          open={isBadNetwork}
          showButton={false}
          text={`${t("Your network connection is unstable and too weak to continue. Please switch to a better network to resume the session."
          )}`}
        />
      )} */}
    </div>
  );
};

export default VideoConsent;
